import { Color } from 'three';

import type { AnalysisSet } from '@paragon/contract-library';

/**
 * These definitions for capacity coloring need to be kept in sync with the server side class in:
 * servers/SealingService/DesignPdfCreation/DesignPdfCreation/CapacityColoring.cs
 */

const colorCapacity20 = new Color(0x16d022);
const colorCapacity40 = new Color(0x93c951);
const colorCapacity60 = new Color(0xf2ea32);
const colorCapacity80 = new Color(0xefac1f);
const colorCapacity100 = new Color(0xf37021);

// The failing capacity color is separate to make sure that a passing capacity ratio is not red.
const colorCapacityFails = new Color(0xff4d52);
const colorPassingNonLoadCapacityCheck = '#eeeeee';
const colorCapacityWorkingChanges = '#64b5f6';

export function getColorFromCapacityRatio(capacityRatio: number) {
  if (capacityRatio <= 0.2) return colorCapacity20;
  if (capacityRatio <= 0.4) return new Color().lerpColors(colorCapacity20, colorCapacity40, (capacityRatio - 0.2) * 5);
  if (capacityRatio <= 0.6) return new Color().lerpColors(colorCapacity40, colorCapacity60, (capacityRatio - 0.4) * 5);
  if (capacityRatio <= 0.8) return new Color().lerpColors(colorCapacity60, colorCapacity80, (capacityRatio - 0.6) * 5);
  if (capacityRatio <= 1) return new Color().lerpColors(colorCapacity80, colorCapacity100, (capacityRatio - 0.8) * 5);

  return colorCapacityFails;
}

function toHexString(color: Color) {
  return `#${color.getHexString()}`;
}

export function getColorHexStringFromCapacityRatio(capacity: number) {
  return toHexString(getColorFromCapacityRatio(capacity));
}

export function getColorForComponentWithWorkingChanges() {
  return colorCapacityWorkingChanges;
}

export function getPassOrFailColorFromAnalysisSet({ capacity, errors }: AnalysisSet): string {
  return getPassOrFailColor({ passes: capacity <= 1 && errors.length === 0 });
}

export function getPassOrFailColor({ passes }: { passes: boolean }): string {
  return toHexString(passes ? colorCapacity20 : colorCapacityFails);
}

export const passingNonLoadCapacityCheckColor = colorPassingNonLoadCapacityCheck;
